import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Loader2, AlertTriangle } from "lucide-react";
import { MessageActionSheet, LongPressBubble, ConversationBlockMenu } from "@/components/MessageActions";
import { HomeownerBlockedList } from "@/components/BlockedSendersCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const hwToken = () => new URLSearchParams(window.location.search).get("t") || "";
const withToken = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(hwToken())}`;

export default function PublicChat() {
  const { requestId } = useParams();
  const [messages, setMessages] = useState([]);
  const [jobInfo, setJobInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const isMine = (m) => m.sender_type === "homeowner";
  const moderate = async (kind, body) => {
    const res = await fetch(withToken(`${API_BASE}/api/moderation/public/${requestId}/${kind}`), {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Something went wrong");
    return res.json();
  };
  const messagesEndRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(withToken(`${API_BASE}/api/requests/${requestId}/public-job-info`));
        const data = await res.json().catch(() => null);
        if (!res.ok) { setError(data?.detail || "Unable to load"); return; }
        setJobInfo(data);
      } catch { setError("Unable to connect."); }
      finally { setLoading(false); }
    })();
  }, [requestId]);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(withToken(`${API_BASE}/api/chat/${requestId}/public-messages`));
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) { console.error(err); }
  }, [requestId]);

  useEffect(() => {
    if (!error) {
      fetchMessages();
      const i = setInterval(fetchMessages, 4000);
      return () => clearInterval(i);
    }
  }, [error, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await fetch(withToken(`${API_BASE}/api/chat/${requestId}/homeowner-message`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), sender_type: "homeowner" }),
      });
      setText("");
      fetchMessages();
    } catch { }
    finally { setSending(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-sm text-zinc-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="public-chat-page">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <MessageSquare className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-sm font-bold text-white">Chat — {jobInfo?.service_type}</p>
            <p className="text-[10px] text-zinc-400">
              Technician: {jobInfo?.technician_name} | {jobInfo?.request_code}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <ConversationBlockMenu messages={messages} isMine={isMine} post={moderate} onChanged={fetchMessages} />
            <HomeownerBlockedList requestId={requestId} token={hwToken()} onChanged={fetchMessages} />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 max-w-lg mx-auto w-full">
        <div className="space-y-2">
          {messages.length === 0 && (
            <p className="text-xs text-zinc-500 text-center py-12">No messages yet</p>
          )}
          {messages.map(msg => {
            const isMe = msg.sender_type === "homeowner";
            return (
              <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                <LongPressBubble onLongPress={() => !isMe && setActionMsg(msg)} data-testid={`public-chat-msg-${msg.id}`}
                  className={`max-w-[80%] px-3 py-2 rounded-xl text-xs select-none ${
                  isMe
                    ? "bg-red-600 text-white rounded-br-sm"
                    : msg.sender_type === "management"
                      ? "bg-blue-900 text-blue-100 rounded-bl-sm"
                      : "bg-zinc-800 text-zinc-200 rounded-bl-sm"
                }`}>
                  {!isMe && <p className="text-[9px] font-bold mb-0.5 opacity-70">{msg.sender_name}</p>}
                  {msg.message_type === "photo" && msg.file_url && (
                    <img src={withToken(`${API_BASE}/api/chat/${requestId}/public-messages/${msg.id}/image`)} alt="Photo" className="w-full max-w-[200px] rounded-lg mb-1" />
                  )}
                  {msg.text && <p className="leading-relaxed">{msg.text}</p>}
                  <p className="text-[9px] mt-1 opacity-50">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </LongPressBubble>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="p-3 border-t border-zinc-800">
        <div className="max-w-lg mx-auto flex gap-2">
          <input type="text" value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") sendMessage(); }}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-full text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-red-500"
            data-testid="public-chat-input"
          />
          <Button onClick={sendMessage} disabled={sending || !text.trim()}
            className="h-10 w-10 p-0 bg-red-600 hover:bg-red-700 rounded-full"
            data-testid="public-chat-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Send className="w-4 h-4 text-white" />}
          </Button>
        </div>
      </div>
      <MessageActionSheet msg={actionMsg} onClose={() => setActionMsg(null)} post={moderate} onChanged={fetchMessages} />
    </div>
  );
}
